import L from 'leaflet';
import { useEffect } from 'react';
import { createElementObject, createOverlayComponent } from '@manifesto-tech/react-leaflet-core';
import { breakpoint } from '../../../../../vendorjs/breakpoint-helper.vendor';

L.CenterPopup = L.Popup.extend({
  options: {
    autoPan: false,
    keepInView: true,
  },
  resizeEnd: setTimeout(() => false),

  initialize(options, source) {
    L.setOptions(this, options);
    L.Popup.prototype.initialize.call(this, options, source);
  },

  getPercentage(num, percentage) {
    return (num * percentage) / 100;
  },

  _initLayout() {
    L.Popup.prototype._initLayout.call(this);
    this._handlers();
  },

  _updatePosition() {
    const map = this._map;

    // Unset content styles.
    this._contentNode.style = {};

    if (map) {
      const size = breakpoint.isMobile ? 100 : 75;
      const mapPanePos = map._mapPane._leaflet_pos || { x: 0, y: 0 };
      const mapWidth = map.getSize().x;
      const mapHeight = map.getSize().y;
      const contentHeight = this._contentNode.offsetHeight;
      const maxPopupHeight = this.getPercentage(mapHeight, size);
      const popupWidth = this.getPercentage(mapWidth, size);
      const popupHeight = contentHeight < maxPopupHeight ? contentHeight : maxPopupHeight;
      const paddingX = mapWidth - popupWidth;
      const paddingY = mapHeight - popupHeight;

      if (this._container?.style) {
        this._container.style.left = `${paddingX / 2 - mapPanePos.x}px`;
        this._container.style.top = `${paddingY / 2 - mapPanePos.y}px`;
        this._container.style.width = `${popupWidth}px`;
        this._container.style.height = breakpoint.isMobile ? '' : `${popupHeight}px`;
        this._container.style.transform = '';
      }

      // Recalculate popup width/height/position once images have loaded.
      const images = this._contentNode.querySelectorAll('img');
      images.forEach((img) => {
        img.addEventListener('load', () => {
          this._updatePosition();
        });
      });
    }
  },

  _handlers() {
    this._map.on(
      'resize',
      () => {
        clearTimeout(this.resizeEnd);
        this.resizeEnd = setTimeout(() => this._updatePosition(), 500);
      },
      this
    );
    this._map.on('popupopen', this._updatePosition, this);
  },
});

L.centerPopup = function (options, source) {
  return new L.CenterPopup(options, source);
};

const CenterPopup = createOverlayComponent(
  (props, context) => {
    const popup = L.centerPopup(props, context.overlayContainer);
    return createElementObject(popup, context);
  },
  (element, context, { position }, setOpen) => {
    const { map, overlayContainer } = context;
    const { instance } = element;

    useEffect(() => {
      function onPopupOpen(event) {
        if (event.popup === instance) {
          map.dragging.disable();
          map.smoothWheelZoom.disable();
          map.zoomControl.remove();
          map.attributionControl.remove();
          setOpen(true);
        }
      }

      function onPopupClose(event) {
        if (event.popup === instance) {
          map.dragging.enable();
          map.smoothWheelZoom.enable();
          map.zoomControl.addTo(map);
          map.attributionControl.addTo(map);
          setOpen(false);
        }
      }

      map.on({
        popupopen: onPopupOpen,
        popupclose: onPopupClose,
      });

      if (!overlayContainer) {
        if (position) {
          instance.setLatLng(position);
        }
        instance.openOn(map);
      } else {
        overlayContainer.bindPopup(instance);
      }

      return () => {
        map.off({
          popupopen: onPopupOpen,
          popupclose: onPopupClose,
        });

        overlayContainer && overlayContainer.unbindPopup();
        map.removeLayer(instance);
      };
    }, [context, element, position, setOpen]);
  }
);

export default CenterPopup;
